import { Injectable } from '@angular/core';
import { HttpClient, HttpResponse } from '@angular/common/http';
import lodash from 'lodash';
import { BehaviorSubject, Observable, of, Subject } from 'rxjs';
import { distinctUntilChanged, map, pluck, switchMap, tap } from 'rxjs/operators';

import { SourceType } from '../../entities/source-type';
import { createRequestOption } from '../model/request.utils';
import { AccountService } from 'app/shared/auth/account.service';
import { OrganizationService } from '../organization';



@Injectable({
  providedIn: 'root'
})
export class ProjectService {
  private readonly _projects$ = new BehaviorSubject<App.Project[]>([]);
  private readonly _trigger$ = new Subject<void>();

  projects$: Observable<App.Project[]> = this._projects$.asObservable();

  constructor(
    private http: HttpClient,
    private accountService: AccountService,
    private organizationService: OrganizationService,
  ) {
    // combineLatest([
    //   accountService.account$,
    //   this._trigger$.pipe(startWith(undefined as void)),
    // ]).pipe(
    //   switchMap(([account]) => {
    //     if (account) {
    //       return this.fetch().pipe(
    //         retryWhen(errors => errors.pipe(
    //           concatMap((error, count) => {
    //             if (count <= 10 && (!error.status || error.status >= 500)) {
    //               return of(error);
    //             }
    //             return throwError(error);
    //           }),
    //           delay(1000),
    //         )),
    //       );
    //     } else {
    //       return of([]);
    //     }
    //   }),
    //   distinctUntilChanged((a, b) => a === b || (a && b && JSON.stringify(a) === JSON.stringify(b))),
    // ).subscribe(
    //   projects => this._projects$.next(projects),
    //   err => this.alertService.error(err.message, null, null),
    // );
  }

  reset() {
    this._trigger$.next();
  }

  create(project: App.Project): Observable<App.Project> {
    return this.http.post(this.projectUrl(), project);
  }

  update(project: App.Project): Observable<App.Project> {
    return this.http.put<App.Project>(this.projectUrl(), project).pipe(
      tap(
        p => this.updateProject(p),
        () => this.reset(),
      ),
    )
  }

  find(projectName: string): Observable<App.Project> {
    return this.projects$.pipe(
      map(projects => projects.find(p => p.projectName === projectName)),
      distinctUntilChanged((a, b) => a === b || JSON.stringify(a) === JSON.stringify(b)),
    );
  }

  findByOrganization(organizationId?: number): Observable<App.Project[]> {
    if (organizationId) {
      return this.projects$.pipe(
        map(projects => projects.filter(p => p.organization.id === organizationId)),
      );
    } else {
      return this.projects$;
    }
  }

  query(req?: any): Observable<HttpResponse<App.GeneralPaginationResponse<App.Project>>> {
    const options = createRequestOption(req);
    return this.http.get<App.GeneralPaginationResponse<App.Project>>(this.projectUrl(), {
      params: options,
      observe: 'response'
    });
  }

  findSourceTypesByName(projectName: string): Observable<SourceType[]> {
    return this.find(projectName).pipe(
      pluck('sourceTypes'),
      switchMap((sourceTypes) => {
        if (sourceTypes) {
          return of(sourceTypes);
        } else {
          return this.http.get<SourceType[]>(`api/projects/${projectName}/source-types`);
        }
      })
    );
  }

  delete(projectName: string): Observable<any> {
    return this.http.delete(this.projectUrl(projectName));
  }

  getSoftwareVersion(): Observable<App.VersionEntry> {
    return this.http.get<App.VersionEntry>(this.projectUrl() + '/software-version');
  }

  getSoftwareVersions(studyId?: number): Observable<Array<App.VersionEntry>> {
    return this.http.get<Array<App.VersionEntry>>(this.projectUrl() + '/software-version/list', {
      params: studyId ? {
        studyId: studyId
      } : undefined
    })
      .pipe(
        map((data) => {
          return lodash.uniqBy(data, 'version');
        })
      );
  }

  private updateProject(project: App.Project) {
    const nextValue = this._projects$.value.slice();
    const idx = nextValue.findIndex(p => p.id === project.id);
    let needsAuthRenewal = false;
    if (idx >= 0) {
      if (nextValue[idx].projectName !== project.projectName) {
        needsAuthRenewal = true;
      }
      nextValue[idx] = project;
    } else {
      nextValue.push(project);
    }
    this._projects$.next(nextValue);
    if (needsAuthRenewal) {
      this.accountService.reset();
    }
  }

  protected projectUrl(projectName?: string, organizationId?: number): string {
    let url = 'api/projects/studies';

    if (projectName) {
      url += '/' + encodeURIComponent(projectName);
    }
    return url;
  }
}
